import { IMPORT_NOT_READY_BY_NEED, type SetupNeed } from "@shared/connectors/setupNeed";
import {
  SkyAccordion,
  SkyAccordionItem,
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyText,
  SkyTooltip,
} from "@skypointcloud/skypoint-ui";
import { useId, useMemo, type ReactElement, type ReactNode } from "react";
import { Link } from "wouter";
import type { DataflowLastRun } from "@shared/types/dataflows";
import type { RunDetail, RunStatus } from "@shared/types/runs";
import { RunTablesGrid } from "@/components/connections/RunTablesGrid";
import { SettledImportLine } from "@/components/connections/ConnectionStatus";
import { ImportProgress, LiveLine } from "@/components/runs/ImportProgress";
import {
  isTerminalRun,
  rowsLineParts,
  runStatusPresentation,
  loadHadNoDestination,
  loadsSendingFiles,
  loadsWaitingForSlot,
  totalsFromRows,
  type RowsLinePart,
} from "@/components/runs/runFormatting";
import {
  useImportAnnouncement,
  useRunActivity,
  type RunActivityResult,
} from "@/hooks/use-run-activity";
import { absolutePath } from "@/lib/routes";
import { PlayIcon } from "@/lib/icons";
import { importStepText, isActivityLive, type RunActivity } from "@/lib/runs/runActivity";
import type { StepVocabularyContext } from "@/lib/runs/stepVocabulary";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useNow } from "@/hooks/use-now";
import { destinationBlockReason, useDestinationReadiness } from "@/hooks/use-destination-readiness";
import { NOT_WHILE_IMPORTING } from "@/components/connections/CancelImportDialog";

/**
 * The connection's last import, on its editor (Session 7, O1; canvas 17 §10; Session 10, P12).
 *
 * The same sentence as the run page, this connection's tables only, and a link to the run. It
 * never offers a retry: retrying is a decision about a run, taken on the run's own page, with the
 * run's steps in front of the person taking it.
 *
 * While the import is queued or running it is the "Import in progress" panel (ux-s10.md §2):
 * the bar, "Step 2 of 3: Loading into Snowflake · 7 of 40 tables", a vertical stepper of this
 * connection's steps (not clickable), and one polite live line that changes only on a step or
 * phase change. It reads `GET /runs/:id` (not audited) and the tab's live feed; the editor hands
 * it the activity it already computed for its header, so the two can never disagree.
 *
 * The live line (review r1, B1) never lives inside a branch that the import's own progress
 * swaps: a region inserted already holding text is usually not read, and one removed in the
 * render that changes its text says nothing, so "finished" or "failed at step 2 of 3" would
 * be lost. With `activity` given, the EDITOR owns the line (`ImportLiveLine`, mounted in its
 * title row before the panel exists) and this card announces nothing. On its own, the card
 * mounts the line once at its frame, the same element on every branch.
 *
 * A run that is gone (purged by retention, or unreadable for this user) reads as a quiet line,
 * not an error bar: the connection is fine, only its history is not there.
 *
 * Round 4 (owner, 2026-09-30, item 4 part 2; the lead's 4b): the card repeated the first row of
 * the imports grid under it, so its tables fold. The title row (the status, when, "View import
 * details") and, while it moves, the import's progress always show; the tables' sentence ("2
 * tables · 1,000 rows extracted · ...") is a `SkyAccordion` toggle over this import's tables. It is
 * open by default while the import is queued or running, the reason the panel exists, and after
 * a failed or partial one; folded after a success. The default is taken once per run
 * (`key={runId}`), so an import the reader watched finish stays as they left it. The live line
 * is never inside the fold: the card's own sits in the frame, the editor's in its title row.
 */

/** Runs that did not refresh everything: the card names the last refresh that did. */
const INCOMPLETE: ReadonlySet<RunStatus> = new Set(["partially_succeeded", "failed", "cancelled"]);

/** Round 4 (4b): an import the reader should see the tables of without asking. */
const OPEN_BY_DEFAULT: ReadonlySet<RunStatus> = new Set([
  "queued",
  "running",
  "failed",
  "partially_succeeded",
]);

/** The fold's one item. */
const TABLES_ITEM = "tables";

/**
 * The tables' sentence as the fold's toggle text: the run page's `RowsLine` in spans, since a
 * toggle is a button and a paragraph cannot sit in one. A failure part keeps the destructive
 * token and its words (never colour alone).
 */
function SummaryLine({ parts }: { parts: RowsLinePart[] }) {
  return (
    <SkyText size={300} weight="semibold" testId="last-run-rows-line">
      {parts.map((part, index) => (
        // The parts are an ordered sentence that is rebuilt whole; the position is the key.
        <span key={index}>
          {index > 0 ? <span className="font-normal text-muted-foreground"> · </span> : null}
          <span className={part.tone === "danger" ? "text-destructive" : undefined}>
            {part.text}
          </span>
        </span>
      ))}
    </SkyText>
  );
}

export interface LastRunPanelProps {
  dataflowId: string;
  lastRunId: string | null;
  /** Kept from the last complete refresh when a run fails or is partial (finalize). */
  lastRefreshAt: string | null;
  /**
   * The activity the editor computed (Session 10, P12): the newest of the start response, the
   * list's row and the detail's `lastRunId` (S4). Without it the panel reads `lastRunId` itself.
   */
  activity?: RunActivityResult;
  /** For the bar's label and the live line. */
  connectionName?: string;
  vocabulary?: StepVocabularyContext;
  /** Offered under "has not run yet" (the editor's Import now, for a writer). */
  neverAction?: ReactNode;
  /**
   * The list row's last run (post-release 607e63a, review r1 S1). Its `outcome` is THIS
   * connection's own part of that run, so the badge says what the list and the header say; it
   * is read only when it names the run this card shows, and only once that run is over.
   */
  listedRun?: Pick<DataflowLastRun, "runId" | "outcome" | "outcomeRunId" | "waiting"> | null;
}

function Frame({
  children,
  title = "Last import",
  aside,
  announcement,
}: {
  children: ReactNode;
  title?: string;
  /**
   * Round 4 (4b): what sits on the title's own line (the status, when, "View import details"),
   * so the folded card is that line and the tables' sentence under it.
   */
  aside?: ReactNode;
  /** The card's own live line (standalone only); null when the host owns it. */
  announcement: string | null;
}) {
  return (
    <SkyCard className="p-4" testId="last-run-panel">
      <div className="flex flex-wrap items-center gap-3">
        <SkyText as="h3" size={300} weight="semibold" block>
          {title}
        </SkyText>
        {aside}
      </div>
      {/* Same position on every branch, so React keeps the one element as the import moves. */}
      {announcement !== null ? (
        <LiveLine text={announcement} testId="last-run-announcement" />
      ) : null}
      {children}
    </SkyCard>
  );
}

/**
 * The editor's one polite live line for its connection's import (review r1, B1). Mounted in
 * the editor's title row for a saved connection, whether or not it has ever imported, so the
 * region exists before "{name} import queued." and stays through "{name} import finished.".
 *
 * It speaks only about an import this page has seen move or this tab started, once per step or
 * phase (`useImportAnnouncement`, review r2 B1'): opening a connection whose last import
 * failed says nothing. `connectionName` is the SAVED name, never the Name field being typed.
 */
export function ImportLiveLine({
  activity,
  connectionName,
}: {
  activity: RunActivity;
  connectionName: string;
}) {
  const text = useImportAnnouncement(activity, connectionName);
  return <LiveLine text={text} testId="connection-import-live" />;
}

/** No read of the run yet: Queued (the start response's run), loading, or no longer available. */
function beforeFirstReadView(
  activity: RunActivity,
  name: string,
  announcement: string | null,
): ReactElement {
  if (activity.phase === "queued") {
    // The start response's run before the first read: Queued, never "Ready" alone.
    return (
      <Frame title="Import in progress" announcement={announcement}>
        <ImportProgress
          activity={activity}
          density="panel"
          connectionName={name}
          announce={false}
          bare
          testId="last-run-live"
        />
      </Frame>
    );
  }
  if (activity.phase === "loading") {
    return (
      <Frame announcement={announcement}>
        <SkyText size={200} color="muted" block role="status" testId="last-run-loading">
          Loading the last import…
        </SkyText>
      </Frame>
    );
  }
  return (
    <Frame announcement={announcement}>
      <SkyText size={200} color="muted" block testId="last-run-unavailable">
        Last import no longer available
      </SkyText>
    </Frame>
  );
}

/**
 * The card's badge: this connection's own outcome when the list has it for this very run (a run
 * whose extract succeeded and whose load failed reads partially succeeded as a whole, but Failed
 * here, as in the header); the run's otherwise.
 */
function shownStatus(
  detail: RunDetail,
  terminal: boolean,
  listedRun: LastRunPanelProps["listedRun"],
): RunStatus {
  // #460 (option 2): an outcome the server read from an earlier settled run (`outcomeRunId`) is
  // not this run's, so the card keeps this run's own status and names the earlier run below.
  if (
    terminal &&
    listedRun?.outcome &&
    !listedRun.outcomeRunId &&
    listedRun.runId === detail.runId
  ) {
    return listedRun.outcome;
  }
  return detail.status;
}

function finishedOrStarted(detail: RunDetail, zone: string): string {
  if (detail.finishedAt) {
    return `Finished ${formatInstant(detail.finishedAt, zone, "short", { named: true })}`;
  }
  return `Started ${formatInstant(detail.startedAt ?? detail.createdAt, zone, "short", { named: true })}`;
}

function noTablesSentence(status: RunStatus): string {
  return isTerminalRun(status)
    ? "This import reported no table for this connection."
    : "No table has reported yet.";
}

export function LastRunPanel({
  dataflowId,
  lastRunId,
  lastRefreshAt,
  activity: given,
  connectionName,
  vocabulary,
  neverAction,
  listedRun,
}: LastRunPanelProps) {
  const zone = useDisplayTz();
  // On its own the card polls a run in flight every 5 s and stops once it is over. The editor
  // passes its own activity instead, which also folds the tab's live feed (15 s poll while live).
  const own = useRunActivity({
    runId: given ? null : lastRunId,
    dataflowId,
    fetchDetail: true,
    vocabulary,
    live: false,
  });
  const activity = given ?? own;
  const detail = activity.detail;
  const runId = activity.runId;
  const name = connectionName ?? "This connection";
  // With `activity` given the host (the editor) owns the live line; on its own, the frame does,
  // with the same rule: only an import seen moving, once per step or phase (review r2 B1').
  const spoken = useImportAnnouncement(activity, name);
  const announcement = given ? null : spoken;

  const rows = useMemo(
    () => (detail?.entities ?? []).filter((row) => row.dataflowId === dataflowId),
    [detail, dataflowId],
  );

  if (!runId) {
    return (
      <Frame announcement={announcement}>
        <SkyText size={200} color="muted" block testId="last-run-never">
          This connection has not been imported yet.
        </SkyText>
        {neverAction ? <div className="mt-2">{neverAction}</div> : null}
      </Frame>
    );
  }

  const live = isActivityLive(activity);

  // Called, not rendered as a component, so the Frame stays the one element on every branch.
  if (!detail) return beforeFirstReadView(activity, name, announcement);

  const terminal = isTerminalRun(detail.status);
  // The card is this connection's, so its badge is this connection's own outcome when the list
  // has it for this very run (a run whose extract succeeded and whose load failed reads
  // partially succeeded as a whole, but Failed here, as in the header); the run's otherwise.
  const shown = shownStatus(detail, terminal, listedRun);
  // #787: a failure that was only a readiness gate's refusal reads Waiting, as the header does.
  const ownListed = listedRun?.runId === detail.runId && !listedRun.outcomeRunId;
  const gated = terminal && ownListed && shown === "failed" && listedRun?.waiting;
  const settledLine =
    terminal && listedRun?.runId === detail.runId ? (
      <SettledImportLine run={listedRun} testId="last-run-settled" />
    ) : null;
  const presentation = gated
    ? { ...runStatusPresentation(shown), label: "Waiting", color: "informative" as const }
    : runStatusPresentation(shown);
  // The run's duration covers every connection in it; the card states it only when this
  // connection is the whole run, so the sentence never pairs one connection's rows with a
  // schedule's time.
  const wholeRun = detail.dataflowIds.length === 1 && detail.dataflowIds[0] === dataflowId;
  // #1127: the rows include the tables the extract planned and has not reached. While the
  // import moves the heading counts against them ("0 of 7 tables started"); a finished import
  // none of whose tables reported says so in words, never a bare dash.
  const totals = totalsFromRows(rows);
  const parts =
    !totals && terminal
      ? [{ text: noTablesSentence(detail.status), tone: "default" as const }]
      : rowsLineParts(totals, {
          durationSeconds: wholeRun ? detail.durationSeconds : null,
          ...(terminal ? {} : { plannedTables: rows.length }),
        });

  return (
    <Frame
      title={live ? "Import in progress" : "Last import"}
      announcement={announcement}
      aside={
        <>
          <SkyBadge appearance="tint" color={presentation.color} testId="last-run-status">
            {presentation.label}
          </SkyBadge>
          <SkyText size={200} color="muted">
            {finishedOrStarted(detail, zone)}
          </SkyText>
          <Link
            href={absolutePath(`/runs/${detail.runId}`)}
            className="ml-auto underline-offset-2 hover:underline"
            aria-label={`View import details for ${name}`}
            data-testid="last-run-link"
          >
            <SkyText size={200} weight="semibold" color="accent">
              View import details
            </SkyText>
          </Link>
        </>
      }
    >
      {live ? (
        <div className="mb-3">
          <ImportProgress
            activity={activity}
            density="panel"
            connectionName={name}
            announce={false}
            bare
            testId="last-run-live"
          />
        </div>
      ) : null}

      {settledLine ? <div className="mb-3">{settledLine}</div> : null}

      {INCOMPLETE.has(shown) && lastRefreshAt ? (
        <SkyText size={200} color="muted" block className="mb-3" testId="last-run-last-complete">
          Last complete refresh {formatInstant(lastRefreshAt, zone, "short", { named: true })}
        </SkyText>
      ) : null}

      {rows.length > 0 ? (
        // Round 4 (4b): the tables fold under their sentence. Open by default while the import
        // moves or after it failed or partly failed, taken once per run (`key`).
        <SkyAccordion
          key={detail.runId}
          defaultOpenItems={OPEN_BY_DEFAULT.has(shown) ? [TABLES_ITEM] : []}
          testId="last-run-details"
          className="-mx-2"
        >
          <SkyAccordionItem
            value={TABLES_ITEM}
            heading={<SummaryLine parts={parts} />}
            panelClassName="px-2 pt-2 pb-0"
          >
            <RunTablesGrid
              rows={rows}
              terminal={terminal}
              noDestination={loadHadNoDestination(detail.steps)}
              waitingLoads={loadsWaitingForSlot(detail.steps)}
              sendingLoads={loadsSendingFiles(detail.steps)}
              runId={detail.runId}
              testId="last-run-tables"
            />
          </SkyAccordionItem>
        </SkyAccordion>
      ) : (
        <SkyText size={200} color="muted" block testId="last-run-no-tables">
          {noTablesSentence(detail.status)}
        </SkyText>
      )}
    </Frame>
  );
}

/**
 * The editor header's import detail, beside the status badge while an import is active: "Queued"
 * or "Step 2 of 3". Since the owner's review of 2026-09-28 the status badge itself says
 * "Running" (it merges the setup state and the run, as the list's Status column does), so this is
 * the smaller second part, the header's version of the list's step line.
 */
export function ImportStepText({ activity }: { activity: RunActivity }) {
  // #1672: "Sending files to Snowflake" carries the time so far, which moves while it sends.
  const now = useNow();
  const text = importStepText(activity, now);
  if (!text) return null;
  return (
    <SkyText size={200} color="muted" testId="header-import-badge">
      {text}
    </SkyText>
  );
}

/** #1081: why Import now and Import with options are off while this connection's import runs. */
export const IMPORT_OFF_WHILE_RUNNING = NOT_WHILE_IMPORTING;
/** 2026-09-28: why Import now is off for a connection turned off by hand. */
export const IMPORT_DISABLED = "This connection is disabled. Enable it to import.";

/**
 * Why Import now is off, or null when it is on: a disabled connection first, then one whose setup
 * is incomplete, then one whose import is already running. The header's button and the Runs
 * view's empty state read the same answer.
 */
export function importUnavailableReason(
  state: "draft" | "ready" | "disabled",
  active: boolean,
  need: SetupNeed = "table",
): string | null {
  if (state === "disabled") return IMPORT_DISABLED;
  if (state !== "ready") return IMPORT_NOT_READY_BY_NEED[need];
  return active ? IMPORT_OFF_WHILE_RUNNING : null;
}

/**
 * #1080, #1081: why an import control is off (Import now and Import with options alike, in the
 * header, its overflow menu and the Connections row menu), or null when it is on: the
 * connection's own reason first (`importUnavailableReason`), then the destination's (#375, #923:
 * the server refuses every run while it is not Ready).
 */
export function importOffReason(
  state: "draft" | "ready" | "disabled",
  active: boolean,
  destinationReason: string | null,
  need: SetupNeed = "table",
): string | null {
  return importUnavailableReason(state, active, need) ?? destinationReason;
}

/**
 * "Import now" in the editor header (P12). The caller renders it only with `runs.start`. It is
 * unavailable, with the reason in a tooltip and for screen readers, while the connection is not
 * Ready or an import is already queued or running. It starts in place: the page stays.
 *
 * Focus never moves (ux-s10.md §2; P12 lead fix round, B1). The element tree is the same in
 * every state, so React never replaces the focused button as an import starts or ends; and the
 * button is `aria-disabled` with its click swallowed rather than natively `disabled`, because a
 * browser drops focus from a control that becomes disabled. The tooltip host is always mounted
 * and is kept closed while there is no reason.
 */
export function ImportNowButton({
  state,
  active,
  pending,
  onImport,
  need = "table",
}: {
  /** The connection's setup state: only `ready` imports; `disabled` says so in its own words. */
  state: "draft" | "ready" | "disabled";
  active: boolean;
  pending: boolean;
  onImport: () => void;
  /** #2481: what the setup still needs, in the connector's words. */
  need?: SetupNeed;
}) {
  return (
    <ImportActionButton
      need={need}
      state={state}
      active={active}
      pending={pending}
      onPress={onImport}
      appearance="primary"
      label="Import now"
      testId="connection-import-now"
    />
  );
}

/**
 * #1080, #1081: "Import with options" in the editor header, off for the same reasons as Import
 * now and saying which, in the same tooltip and screen reader text.
 */
export function ImportOptionsButton({
  state,
  active,
  pending,
  onOpen,
  need = "table",
}: {
  state: "draft" | "ready" | "disabled";
  active: boolean;
  pending: boolean;
  onOpen: () => void;
  need?: SetupNeed;
}) {
  return (
    <ImportActionButton
      need={need}
      state={state}
      active={active}
      pending={pending}
      onPress={onOpen}
      appearance="secondary"
      label="Import with options"
      testId="connection-import-options"
    />
  );
}

function ImportActionButton({
  state,
  active,
  pending,
  onPress,
  appearance,
  label,
  testId,
  need = "table",
}: {
  need?: SetupNeed;
  state: "draft" | "ready" | "disabled";
  active: boolean;
  pending: boolean;
  onPress: () => void;
  appearance: "primary" | "secondary";
  label: string;
  testId: string;
}) {
  const reasonId = useId();
  // #375, #923: the server refuses a run while the destination is not Ready; said here first.
  const reason = importOffReason(
    state,
    active,
    destinationBlockReason(useDestinationReadiness()),
    need,
  );
  const unavailable = reason !== null || pending;
  return (
    <span className="inline-flex">
      <SkyTooltip content={reason ?? ""} open={reason ? undefined : false}>
        <SkyButton
          appearance={appearance}
          icon={appearance === "primary" ? PlayIcon : undefined}
          aria-disabled={unavailable ? true : undefined}
          aria-busy={pending ? true : undefined}
          aria-describedby={reason ? reasonId : undefined}
          className={unavailable ? "cursor-not-allowed opacity-50" : undefined}
          onClick={() => {
            if (!unavailable) onPress();
          }}
          testId={testId}
        >
          {label}
        </SkyButton>
      </SkyTooltip>
      <span id={reasonId} className="sr-only" data-testid={`${testId}-reason`}>
        {reason ?? ""}
      </span>
    </span>
  );
}
